import _ from 'lodash';
import {useEffect} from 'react';

import {envIsServerDeployment} from '../../config';
import * as Generated from '../../generated/graphql';
import {useDeploymentIdQuery} from '../../generated/graphql';
import {identifyWithViewer, trackWBReferrer} from '../../util/analytics';
import {useQuery} from '../graphql/apolloWithAbort';
import {useDispatch, useSelector} from '../hooks';
import * as Actions from './actions';
import * as Selectors from './selectors';
import type * as Types from './types';

// This should be used everywhere except in app. It always returns valid viewer
// App.tsx blocks the rest of the app from loading until useInitViewer is complete.
let lastComparedPhotoUrl = 0;
export function useViewer() {
  return useSelector(Selectors.getViewer, (left, right) => {
    // Avoid hitting the conditional below, because if we do it means that the
    // equality comparison on `_.isEqual` will be called, and because the two
    // values are not reference identical anymore (both are newly-generated
    // objects with 'photoUrl' omitted), the long-path comparison in lodash
    // will be used. But if the two values _are_ reference identical, they will
    // have identical photoUrls anyway. -- NH
    if (left === right) {
      return true;
    }

    const now = Date.now();
    const recomparePhotoUrlInterval = 1000 * 60 * 30; // 30 minutes.
    if (now - lastComparedPhotoUrl > recomparePhotoUrlInterval) {
      // Every 30m make sure to compare the photoUrl as well.
      lastComparedPhotoUrl = now;
      return _.isEqual(left, right);
    }

    // A workaround to deal with the fact that profile photos are signed URLs
    // and thus unstable. Without this check, this selector re-fires on every
    // mutation or query that pulls the viewer down.
    return _.isEqual(_.omit(left, 'photoUrl'), _.omit(right, 'photoUrl'));
  });
}

// This is only called from App.tsx, it populates the redux store with the viewer
// object and the app hangs until we've set loading to false.  This is how we
// know if we're logged in.
export function useInitViewer() {
  const {loading, data, refetch} = useQuery<
    Generated.ViewerQuery,
    Generated.ViewerQueryVariables
  >(Generated.ViewerDocument);

  const viewer =
    data != null && data.viewer != null
      ? (data.viewer as unknown as Types.Viewer)
      : undefined;

  const deploymentIdQuery = useDeploymentIdQuery({
    skip: !envIsServerDeployment,
  });
  const deploymentId = deploymentIdQuery.data?.serverInfo?.deploymentId;

  const reload = useSelector(Selectors.reloadViewer);
  const dispatch = useDispatch();

  // Reload the viewer when set to true
  useEffect(() => {
    if (reload) {
      refetch();
    }
  }, [reload, refetch]);

  useEffect(() => {
    dispatch(Actions.setCurrentViewer(loading, viewer));
    identifyWithViewer(viewer, deploymentId);

    if (!loading) {
      // We send the track event after identifying the currently logged in user with `identifyWithViewer`
      trackWBReferrer();
    }
  }, [dispatch, viewer, deploymentId, loading]);
}
